import { useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import {
	getSiteAssetRegisters,
	getSiteAssetRegistersCount,
} from "services/clients/sites/siteAssetRegisters";
import { defaultPageSize } from "helpers/utils";
import useTreeExpansion from "./useTreeExpansion";
import buildSearchTree from "./buildSearchTree";

const PARENT_ID_ROOTS = 0;

const useGroupedAssetData = (siteId, customCaptions, level, grouping) => {
	const dispatch = useDispatch();
	const [rootData, setRootData] = useState([]);
	const [rootCount, setRootCount] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [expandedRows, setExpandedRows] = useState({});
	const [childData, setChildData] = useState({});
	const [childLoading, setChildLoading] = useState({});

	const childDataRef = useRef(childData);
	childDataRef.current = childData;
	const rootDataRef = useRef(rootData);
	rootDataRef.current = rootData;

	const lastFetchRef = useRef({ sortField: "", sortOrder: "", searchText: "" });
	const flatResultsRef = useRef([]);

	const isSiteAssetMode = grouping === "siteAsset";

	const transformData = useCallback((items) => {
		return items.map((item) => ({
			id: item.id,
			assetNumber: item.assetNumber,
			reference: item.reference,
			description: item.description,
			plannerGroup: item.plannerGroup,
			workCenter: item.workCenter,
			level: item.level,
			parentId: item.parentId,
			rootAssetId: item.rootAssetID,
			code: item.code,
			hasChildren: item.hasChildren,
			oldSiteAssetID: item.oldSiteAssetID,
		}));
	}, []);

	// In Site Asset mode, filter out children that are roots of a different Site Asset
	const findRootAssetId = useCallback((rowId) => {
		const rootRow = rootDataRef.current.find((r) => r.id === rowId);
		if (rootRow) return rootRow.rootAssetId;
		for (const siblings of Object.values(childDataRef.current)) {
			const found = siblings.find((r) => r.id === rowId);
			if (found) return found.rootAssetId;
		}
		return null;
	}, []);

	const childFilter = useCallback(
		(parentId, children) => {
			if (!isSiteAssetMode) return children;
			const parentRootAssetId = findRootAssetId(parentId);
			if (parentRootAssetId == null) return children;
			return children.filter((c) => c.rootAssetId === parentRootAssetId);
		},
		[isSiteAssetMode, findRootAssetId]
	);

	const tree = useTreeExpansion(siteId, transformData, childDataRef, childFilter);

	// Build root fetch params based on grouping mode and level filter
	const getRootParams = useCallback(() => {
		// Flat mode: no filtering by parent/level/rootsOnly
		if (!grouping) {
			return { parentId: undefined, level: undefined, rootsOnly: false };
		}
		if (isSiteAssetMode) {
			// Site Asset mode: roots are items where ID = RootAssetID
			return { parentId: undefined, level: undefined, rootsOnly: true };
		}
		// FLOC mode
		if (level && level > 1) {
			return { parentId: undefined, level, rootsOnly: false };
		}
		return { parentId: PARENT_ID_ROOTS, level: undefined, rootsOnly: false };
	}, [level, isSiteAssetMode, grouping]);

	const fetchRootAssets = useCallback(
		async (pageNo = 1, sortField = "", sortOrder = "", searchText = "") => {
			try {
				setIsLoading(true);
				lastFetchRef.current = { sortField, sortOrder, searchText };
				const isSearching = searchText && searchText.trim() !== "";
				const { parentId, level: lvl, rootsOnly } = isSearching
					? { parentId: undefined, level: undefined, rootsOnly: false }
					: getRootParams();

				const [assetsResponse, countResponse] = await Promise.all([
					getSiteAssetRegisters(siteId, pageNo, defaultPageSize(), searchText, sortField, sortOrder, parentId, lvl, rootsOnly),
					getSiteAssetRegistersCount(siteId, searchText, parentId, lvl, rootsOnly),
				]);

				if (!assetsResponse.status) throw new Error(assetsResponse);

				let transformedData = transformData(assetsResponse.data);
				// In Site Asset mode, blank self-roots can never have same-rootAssetId children,
				// so strip hasChildren to prevent misleading expand chevrons
				if (isSiteAssetMode && !isSearching) {
					transformedData = transformedData.map((r) =>
						!r.assetNumber || r.assetNumber.trim() === ""
							? { ...r, hasChildren: false }
							: r
					);
				}
				if (isSearching && grouping) {
					flatResultsRef.current = transformedData;
					const searchTree = buildSearchTree(transformedData, grouping);
					setRootData(searchTree.rootData);
					setChildData(searchTree.childData);
					setExpandedRows(searchTree.expandedRows);
				} else {
					flatResultsRef.current = [];
					setRootData(transformedData);
					setExpandedRows({});
					setChildData({});
				}
				setRootCount(countResponse.status ? countResponse.data : 0);

				return { success: true, data: transformedData };
			} catch (err) {
				dispatch(showError(`Failed to fetch ${customCaptions?.assetPlural || "assets"}.`));
				return { success: false, error: err };
			} finally {
				setIsLoading(false);
			}
		},
		[siteId, dispatch, customCaptions, transformData, getRootParams, isSiteAssetMode, grouping]
	);

	const searchRootAssets = useCallback(
		async (searchText, sortField, sortOrder) => {
			try {
				setIsLoading(true);
				lastFetchRef.current = { sortField, sortOrder, searchText };

				// Search across ALL assets — don't filter by parentId, level, or rootsOnly
				const [response, countResponse] = await Promise.all([
					getSiteAssetRegisters(siteId, 1, defaultPageSize(), searchText, sortField, sortOrder),
					getSiteAssetRegistersCount(siteId, searchText),
				]);

				if (!response.status) throw new Error(response);

				const transformedData = transformData(response.data);

				if (grouping) {
					flatResultsRef.current = transformedData;
					const searchTree = buildSearchTree(transformedData, grouping);
					setRootData(searchTree.rootData);
					setChildData(searchTree.childData);
					setExpandedRows(searchTree.expandedRows);
				} else {
					flatResultsRef.current = [];
					setRootData(transformedData);
					setExpandedRows({});
					setChildData({});
				}
				setRootCount(countResponse.status ? countResponse.data : 0);

				return transformedData;
			} catch (err) {
				dispatch(showError(`Failed to search ${customCaptions?.assetPlural || "assets"}.`));
				return [];
			} finally {
				setIsLoading(false);
			}
		},
		[siteId, dispatch, customCaptions, transformData, grouping]
	);

	const loadMoreRootAssets = useCallback(
		async (pageNo, prevData, sortOptions, searchText) => {
			try {
				const isSearching = searchText && searchText.trim() !== "";
				const { parentId, level: lvl, rootsOnly } = isSearching
					? { parentId: undefined, level: undefined, rootsOnly: false }
					: getRootParams();
				const response = await getSiteAssetRegisters(
					siteId, pageNo, defaultPageSize(), searchText,
					sortOptions?.sortField, sortOptions?.sortOrder, parentId, lvl, rootsOnly
				);
				if (!response.status) throw new Error(response);

				let newItems = transformData(response.data);
				if (isSiteAssetMode && !isSearching) {
					newItems = newItems.map((r) =>
						!r.assetNumber || r.assetNumber.trim() === ""
							? { ...r, hasChildren: false }
							: r
					);
				}
				if (isSearching && grouping) {
					flatResultsRef.current = [...flatResultsRef.current, ...newItems];
					const searchTree = buildSearchTree(flatResultsRef.current, grouping);
					setRootData(searchTree.rootData);
					setChildData(searchTree.childData);
					setExpandedRows(searchTree.expandedRows);
					return { success: true, data: searchTree.rootData };
				} else {
					const combinedData = [...prevData, ...newItems];
					setRootData(combinedData);
					return { success: true, data: combinedData };
				}
			} catch (err) {
				dispatch(showError(`Failed to load more ${customCaptions?.assetPlural || "assets"}.`));
				return { success: false, error: err };
			}
		},
		[siteId, dispatch, customCaptions, transformData, getRootParams, isSiteAssetMode, grouping]
	);

	const toggleExpand = useCallback(
		async (parentId) => {
			if (expandedRows[parentId]) {
				setExpandedRows((prev) => {
					const next = { ...prev };
					const collapse = (id) => {
						delete next[id];
						(childDataRef.current[id] || []).forEach((child) => {
							if (next[child.id]) collapse(child.id);
						});
					};
					collapse(parentId);
					return next;
				});
				return;
			}

			setExpandedRows((prev) => ({ ...prev, [parentId]: true }));
			if (childDataRef.current[parentId]) return;

			setChildLoading((prev) => ({ ...prev, [parentId]: true }));
			try {
				const response = await getSiteAssetRegisters(siteId, null, null, "", "", "", parentId);
				if (!response.status) throw new Error(response);

				const children = childFilter(parentId, transformData(response.data));
				setChildData((prev) => ({ ...prev, [parentId]: children }));
				childDataRef.current = { ...childDataRef.current, [parentId]: children };
			} catch (err) {
				dispatch(showError(`Failed to load child ${customCaptions?.assetPlural || "assets"}.`));
			} finally {
				setChildLoading((prev) => ({ ...prev, [parentId]: false }));
			}
		},
		[siteId, expandedRows, dispatch, customCaptions, transformData, childFilter]
	);

	const refreshData = useCallback(async () => {
		const { sortField, sortOrder, searchText } = lastFetchRef.current;
		setChildData({});
		setExpandedRows({});
		return fetchRootAssets(1, sortField, sortOrder, searchText);
	}, [fetchRootAssets]);

	// In grouped search mode, rootData is the rendered tree of groups (shorter
	// than the flat results) while rootCount is the count of flat matches. The
	// parent needs the flat-load count to decide whether more pages exist.
	const loadedCount = flatResultsRef.current.length > 0
		? flatResultsRef.current.length
		: rootData.length;

	return {
		data: rootData,
		count: rootCount,
		loadedCount,
		isLoading,
		expandedRows,
		childData,
		childLoading,
		bulkLoading: tree.bulkLoading,
		fetchAssets: fetchRootAssets,
		searchAssets: searchRootAssets,
		loadMoreAssets: loadMoreRootAssets,
		toggleExpand,
		expandAll: () => tree.expandAll(rootData, setExpandedRows, setChildData, setChildLoading),
		collapseAll: () => tree.collapseAll(setExpandedRows),
		refreshData,
	};
};

export default useGroupedAssetData;
